import * as React from "react";
import { useQueryClient } from "@tanstack/react-query";
import type { TaskRecord } from "@nexus/contracts";

import { invalidatePersonaOpportunitySets } from "@/lib/queries/persona-opportunity-sets.queries";
import { detectFinishedPersonaSetGenerationIds } from "../lib/persona-set-generation-task";

/**
 * When a generate-set task hits progress 1 (or leaves the active list), refetch
 * list + detail immediately instead of waiting for the GENERATING poll tick.
 */
export function useInvalidatePersonaSetsOnGenerationTaskEnd(
  tasks: readonly TaskRecord[],
  tenantId: string | null | undefined,
): void {
  const queryClient = useQueryClient();
  const inProgressRef = React.useRef<Set<string>>(new Set());
  const trackedTenantRef = React.useRef(tenantId);

  // Drop in-flight ids on tenant switch so a filtered-out task is not treated as a finish.
  if (trackedTenantRef.current !== tenantId) {
    trackedTenantRef.current = tenantId;
    inProgressRef.current = new Set();
  }

  React.useEffect(() => {
    const scopedTasks = tenantId
      ? tasks.filter((task) => task.tenantId === tenantId)
      : [];
    const { inProgress, finishedIds } = detectFinishedPersonaSetGenerationIds(
      inProgressRef.current,
      scopedTasks,
    );
    inProgressRef.current = inProgress;
    if (finishedIds.length === 0) return;
    invalidatePersonaOpportunitySets(queryClient, tenantId);
  }, [queryClient, tasks, tenantId]);
}
